import { css } from 'styled-components';

// Constants
import {
	COLORS, SCREEN, SCREEN_RATIO, TEXT_COLORS,
} from './constants';

import { parseSpace } from './helpers';
import { FONT_SIZE } from './font';

// Text props
export const textAlign = css`
	${props => (props.textAlign && `text-align: ${props.textAlign}`)};
`;

export const fontWeight = css`
	${props => props.fontWeight && `font-weight: ${props.fontWeight};`}
`;

export const ellipsis = css`
	${props => props.ellipsis && css`
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: ${props.ellipsis};
	`};
`;

export const dontWrapWhiteSpace = css`
	${props => (props.dontWrapWhiteSpace && 'white-space: nowrap')};
`;

export const setWidth = css`
	${props => (props.setWidth && `width: ${props.setWidth}`)};
`;

export const setHeight = css`
	${props => (props.setHeight && `height: ${props.setHeight}`)};
`;

// Max width
export const maxWidth = css`
	${props => (props.maxWidth && `max-width: ${props.maxWidth}`)};
`;
export const minWidth = css`
	${props => (props.minWidth && `min-width: ${props.minWidth}`)};
`;

export const padding = css`
	${(props) => {
		if (props.padding) {
			const {
				top = 0, left = 0, right = 0, bottom = 0,
			} = parseSpace(props.padding);

			return css`
					padding: ${top}px ${right}px ${bottom}px ${left}px;
					
					${props.scale && css`
						${SCREEN.LARGE} {
							padding: ${top / SCREEN_RATIO.LARGE}px ${right / SCREEN_RATIO.LARGE}px ${bottom / SCREEN_RATIO.LARGE}px ${left / SCREEN_RATIO.LARGE}px;
						}
						${SCREEN.MEDIUM} {
							padding: ${top / SCREEN_RATIO.MEDIUM}px ${right / SCREEN_RATIO.MEDIUM}px ${bottom / SCREEN_RATIO.MEDIUM}px ${left / SCREEN_RATIO.MEDIUM}px;
						}
						${SCREEN.SMALL} {
							padding: ${top / SCREEN_RATIO.SMALL}px ${right / SCREEN_RATIO.SMALL}px ${bottom / SCREEN_RATIO.SMALL}px ${left / SCREEN_RATIO.SMALL}px;
						}
					`};
				`;
		}
		return null;
	}};
`;

export const margin = css`
	${(props) => {
		if (props.margin) {
			const {
				top = 0, left = 0, right = 0, bottom = 0,
			} = parseSpace(props.margin);

			let simbol = '';
			if (props.negative) {
				simbol = '-';
			}
			return css`
				margin: ${simbol}${top}px ${simbol}${right}px ${simbol}${bottom}px ${simbol}${left}px;
				
				${props.scale && css`
					${SCREEN.LARGE} {
						margin: ${simbol}${top / SCREEN_RATIO.LARGE}px ${simbol}${right / SCREEN_RATIO.LARGE}px ${simbol}${bottom / SCREEN_RATIO.LARGE}px ${simbol}${left / SCREEN_RATIO.LARGE}px;
					}
					${SCREEN.MEDIUM} {
						margin: ${simbol}${top / SCREEN_RATIO.MEDIUM}px ${simbol}${right / SCREEN_RATIO.MEDIUM}px ${simbol}${bottom / SCREEN_RATIO.MEDIUM}px ${simbol}${left / SCREEN_RATIO.MEDIUM}px;
					}
					${SCREEN.SMALL} {
						margin: ${simbol}${top / SCREEN_RATIO.SMALL}px ${simbol}${right / SCREEN_RATIO.SMALL}px ${simbol}${bottom / SCREEN_RATIO.SMALL}px ${simbol}${left / SCREEN_RATIO.SMALL}px;
					}
				`};
			`;
		}
		return null;
	}};
`;

export const textColors = css`
	${(props) => {
		if (props.text_color) {
			const color = TEXT_COLORS[props.text_color.toUpperCase()];
			if (color) {
				return `color: ${color}};`;
			}
		}
		return null;
	}}
`;

export const elements = css`
	${(props) => {
		if (props.elements && props.elements > 0) {
			const elementWidth = Math.round(100 / props.elements);
			return css`
				> * {
					max-width: ${elementWidth}%;
					width: 100%;
					${SCREEN.SMALL} {
						max-width: 100%;
					}
				}
			`;
		}
		return null;
	}}
`;

export const fontSize = css`
	${(props) => {
		if (props.fontSize) {
			if (FONT_SIZE[props.fontSize.toUpperCase()]) {
				return FONT_SIZE[props.fontSize.toUpperCase()];
			}
		}

		return null;
	}}
`;

export const fluidElement = css`
	${props => props.fluid && css`
		width: 100%;
		min-width: auto;
	`};
`;

export const autoElement = css`
	${props => props.auto && css`
		width: auto;
		min-width: auto;
	`};
`;

const MOBILE_OPTIONS = {
	FLUID: css`
		width: 100%;
		min-width: auto;
	`,
	CENTER: css`
		text-align: center;
	`,
	WRAP: css`
		flex-wrap: wrap;
	`,
	NO_SIDE_SPACING: css`
		margin-right: 0;
		margin-left: 0;
		padding-left: 0;
		padding-right: 0;
	`,
	SEPARATE_CHILDREN: css`
		> *:not(:first-child) {
			margin-top: 40px;
		}
	`,
	COLUMN_REVERSE: css`
		flex-direction: column-reverse;
	`,
	ROW_REVERSE: css`
		flex-direction: row-reverse;
	`,
	HIDE_FIRST: css`
		> :first-child {
			display: none;
		}
	`,
};

export const mobileOptions = css`
	${(props) => {
		if (props.mobile) {
			const options = props.mobile.split(' ');

			return css`
				${SCREEN.SMALL} {
					${options.map(option => MOBILE_OPTIONS[option.split('-').join('_').toUpperCase()])};
				}
			`;
		}
		return null;
	}};
`;

const ON_HOVER_EFFECTS = {
	TRANSITION: css`
		transition: all 0.3s;
	`,
	BC_GRAY_LIGHTER: css`
		background-color: ${COLORS.GRAY_LIGHTER};
	`,
	BORDER_BLUE_DARK: css`
		border: 1px solid ${COLORS.BLUE_DARK};
	`,
	SHADOW: css`
		box-shadow: 0 0 11px rgba(33, 33, 33, 0.2);
	`,
	CURSOR: css`
		cursor: pointer;
	`,
};

export const hoverEffects = css`
	${(props) => {
		if (props.hoverEffects) {
			const options = props.hoverEffects.split(' ');

			return css`
				&:hover {
					${options.map(option => ON_HOVER_EFFECTS[option.split('-').join('_').toUpperCase()])};
				}
			`;
		}
		return null;
	}};
`;
